import React, { useState, useEffect, useRef } from "react";
import "./ArticlePost.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RichTextEditor } from "../../components/RichTextWithMention.jsx";
import { useContributorAuth } from "../../context/ContributorAuthContext.jsx";

const PhotoUpload = ({ photo, onPhotoChange }) => {
  const fileInputRef = useRef(null);

  const handleClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="flex items-center justify-center w-full">
      <label
        htmlFor="photo-upload"
        className="flex flex-col items-center justify-center w-24 h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        {photo ? (
          <img
            src={URL.createObjectURL(photo)}
            alt="Uploaded"
            className="w-full h-full object-cover rounded-lg"
          />
        ) : (
          <div className="flex flex-col items-center justify-center">
            <i className="fas fa-camera text-gray-500 text-lg mb-1"></i>
            <span className="text-xs text-gray-500">Upload</span>
          </div>
        )}
        <input
          ref={fileInputRef}
          id="photo-upload"
          type="file"
          accept="image/*"
          onChange={(e) => {
            if (e.target.files[0]) {
              onPhotoChange(e.target.files[0]);
            }
          }}
          className="hidden"
        />
      </label>
    </div>
  );
};

const ArticlePost = () => {
  const [sportsCategories, setSportsCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMainHeading, setSelectedMainHeading] = useState("");
  const [mainDescription, setMainDescription] = useState("");
  const [mainPhotos, setMainPhotos] = useState([
    { photo: null, description: "" },
  ]);
  const [subHeadings, setSubHeadings] = useState([
    {
      heading: "",
      description: "",
      photos: [{ photo: null, description: "" }],
    },
  ]);
  const [references, setReferences] = useState([""]);

  const navigate = useNavigate();
  const { contributor, loading } = useContributorAuth();

  useEffect(() => {
    if (!loading && !contributor) {
      navigate("/loginContributor", { replace: true });
      return;
    }
    fetch(`${process.env.REACT_APP_BASE_URL}/api/sports/categories`)
      .then((response) => response.json())
      .then((data) => setSportsCategories(data))
      .catch((err) =>
        console.error("Error fetching sports categories: " + err)
      );
  }, [contributor, loading]);

  const handleMainPhotoAdd = () => {
    setMainPhotos([...mainPhotos, { photo: null, description: "" }]);
  };

  const handleMainPhotoChange = (index, field, value) => {
    const updatedPhotos = mainPhotos.map((photo, i) =>
      i === index ? { ...photo, [field]: value } : photo
    );
    setMainPhotos(updatedPhotos);
  };

  const handleSubHeadingAdd = () => {
    setSubHeadings([
      ...subHeadings,
      {
        heading: "",
        description: "",
        photos: [{ photo: null, description: "" }],
      },
    ]);
  };

  const handleSubHeadingChange = (index, field, value) => {
    const updatedSubHeadings = subHeadings.map((subHeading, i) =>
      i === index ? { ...subHeading, [field]: value } : subHeading
    );
    setSubHeadings(updatedSubHeadings);
  };

  const handleSubHeadingPhotoAdd = (subHeadingIndex) => {
    const updatedSubHeadings = subHeadings.map((subHeading, index) =>
      index === subHeadingIndex
        ? {
            ...subHeading,
            photos: [...subHeading.photos, { photo: null, description: "" }],
          }
        : subHeading
    );
    setSubHeadings(updatedSubHeadings);
  };

  const handleSubHeadingPhotoChange = (
    subHeadingIndex,
    photoIndex,
    field,
    value
  ) => {
    const updatedSubHeadings = subHeadings.map((subHeading, i) =>
      i === subHeadingIndex
        ? {
            ...subHeading,
            photos: subHeading.photos.map((photo, j) =>
              j === photoIndex ? { ...photo, [field]: value } : photo
            ),
          }
        : subHeading
    );
    setSubHeadings(updatedSubHeadings);
  };

  const handleReferenceAdd = () => {
    setReferences([...references, ""]);
  };

  const handleReferenceChange = (index, value) => {
    const updatedReferences = references.map((ref, i) =>
      i === index ? value : ref
    );
    setReferences(updatedReferences);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!selectedCategory) {
      toast.error("Please select a sports category");
      return;
    }
    if (!selectedMainHeading.trim()) {
      toast.error("Main heading is required");
      return;
    }
    if (!mainDescription.trim()) {
      toast.error("Description is required");
      return;
    }

    const formData = new FormData();
    formData.append("category", selectedCategory);
    formData.append("mainHeading", selectedMainHeading);
    formData.append("mainDescription", mainDescription);

    // For main photos
    mainPhotos.forEach((photo, index) => {
      formData.append("photos", photo.photo);
      formData.append("photoDescriptions", photo.description);
    });

    // For subheading photos
    subHeadings.forEach((subHeading, subIndex) => {
      formData.append(`subHeadings[${subIndex}][heading]`, subHeading.heading);
      formData.append(
        `subHeadings[${subIndex}][description]`,
        subHeading.description
      );
      subHeading.photos.forEach((photo, photoIndex) => {
        if (photo.photo) {
          formData.append("subHeadingPhotos", photo.photo);
          formData.append("subHeadingPhotoDescriptions", photo.description);
          formData.append(
            "subHeadingPhotoIndices",
            `${subIndex}-${photoIndex}`
          );
        }
      });
    });

    references.forEach((reference, index) => {
      formData.append(`references[${index}]`, reference);
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/article/submit-article`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        }
      );

      if (response.ok) {
        toast.success("Article submitted successfully!");
        navigate("/contributor-dashboard");
      } else {
        toast.error("Failed to submit article. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting article:", error);
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <img
            src="/logo.png"
            alt="Logo"
            className="h-[50px] sm:h-[60px] mx-auto mb-4 sm:mb-6 cursor-pointer hover:opacity-80 transition-opacity drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
            onClick={() => navigate(-1)}
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-[#3498db] mb-2">
            Article Posting
          </h1>
          <p className="text-[#57606f] mt-2 text-sm sm:text-base">
            Create and submit your sports article
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.05)] rounded-2xl p-4 sm:p-8 space-y-6 sm:space-y-8 backdrop-blur-[10px] border border-white/20"
        >
          {/* Basic Information */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Article Details
            </h2>

            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sports Category <span className="text-red-500">*</span>
                </label>
                <select
                  id="sportsCategory"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                >
                  <option value="">Select a category</option>
                  {sportsCategories.map((category, index) => (
                    <option key={index} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main Heading <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="mainHeading"
                  value={selectedMainHeading}
                  onChange={(e) => setSelectedMainHeading(e.target.value)}
                  placeholder="Enter main heading"
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <RichTextEditor
                  value={mainDescription}
                  onChange={setMainDescription}
                />
              </div>
            </div>
          </div>

          {/* Main Photos */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Main Photos{" "}
              <span className="text-gray-500 text-sm font-normal">
                (Optional)
              </span>
            </h2>

            <div className="space-y-4">
              {mainPhotos.map((photo, index) => (
                <div
                  key={index}
                  className="border border-[#ddd] rounded-lg p-4"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <PhotoUpload
                      photo={photo.photo}
                      onPhotoChange={(file) =>
                        handleMainPhotoChange(index, "photo", file)
                      }
                    />
                    {mainPhotos.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updatedPhotos = mainPhotos.filter(
                            (_, i) => i !== index
                          );
                          setMainPhotos(updatedPhotos);
                        }}
                        className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Photo description"
                    value={photo.description}
                    onChange={(e) =>
                      handleMainPhotoChange(
                        index,
                        "description",
                        e.target.value
                      )
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={handleMainPhotoAdd}
                className="w-full h-11 px-4 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
              >
                + Add More Photos
              </button>
            </div>
          </div>

          {/* Sub-Headings */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Sub-Headings{" "}
              <span className="text-gray-500 text-sm font-normal">
                (Optional)
              </span>
            </h2>

            <div className="space-y-4 sm:space-y-6">
              {subHeadings.map((subHeading, subIndex) => (
                <div
                  key={subIndex}
                  className="border border-[#ddd] rounded-lg p-4 sm:p-6 space-y-4"
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="text"
                      placeholder="Sub-Heading"
                      value={subHeading.heading}
                      onChange={(e) =>
                        handleSubHeadingChange(
                          subIndex,
                          "heading",
                          e.target.value
                        )
                      }
                      className="flex-1 h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    />
                    {subHeadings.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updatedSubHeadings = subHeadings.filter(
                            (_, i) => i !== subIndex
                          );
                          setSubHeadings(updatedSubHeadings);
                        }}
                        className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <textarea
                    placeholder="Description"
                    value={subHeading.description}
                    onChange={(e) =>
                      handleSubHeadingChange(
                        subIndex,
                        "description",
                        e.target.value
                      )
                    }
                    rows="4"
                    className="w-full px-3 py-2 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sub-Heading Photos{" "}
                      <span className="text-gray-500">(Optional)</span>
                    </label>
                    <div className="space-y-4">
                      {subHeading.photos.map((photo, photoIndex) => (
                        <div
                          key={photoIndex}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-center gap-4 mb-4">
                            <PhotoUpload
                              photo={photo.photo}
                              onPhotoChange={(file) =>
                                handleSubHeadingPhotoChange(
                                  subIndex,
                                  photoIndex,
                                  "photo",
                                  file
                                )
                              }
                            />
                            {subHeading.photos.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedSubHeadings = subHeadings.map(
                                    (sub, i) =>
                                      i === subIndex
                                        ? {
                                            ...sub,
                                            photos: sub.photos.filter(
                                              (_, j) => j !== photoIndex
                                            ),
                                          }
                                        : sub
                                  );
                                  setSubHeadings(updatedSubHeadings);
                                }}
                                className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            placeholder="Photo description"
                            value={photo.description}
                            onChange={(e) =>
                              handleSubHeadingPhotoChange(
                                subIndex,
                                photoIndex,
                                "description",
                                e.target.value
                              )
                            }
                            className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleSubHeadingPhotoAdd(subIndex)}
                        className="w-full h-11 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-300 font-semibold hover:scale-105 active:scale-100"
                      >
                        + Add More Photos
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={handleSubHeadingAdd}
                className="w-full h-11 px-4 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
              >
                + Add New Sub-Heading
              </button>
            </div>
          </div>

          {/* References */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              References{" "}
              <span className="text-gray-500 text-sm font-normal">
                (Optional)
              </span>
            </h2>

            <div className="space-y-4">
              {references.map((reference, index) => (
                <div key={index} className="flex items-center gap-4">
                  <input
                    type="text"
                    placeholder="Reference"
                    value={reference}
                    onChange={(e) =>
                      handleReferenceChange(index, e.target.value)
                    }
                    className="flex-1 h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  {references.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const updatedReferences = references.filter(
                          (_, i) => i !== index
                        );
                        setReferences(updatedReferences);
                      }}
                      className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={handleReferenceAdd}
                className="w-full h-11 px-4 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
              >
                + Add New Reference
              </button>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="pt-4 sm:pt-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="submit"
                className="flex-1 h-11 flex justify-center items-center border border-transparent rounded-lg text-sm sm:text-base font-semibold text-white bg-[#3498db] hover:bg-[#2980b9] focus:outline-none transition-all duration-300 uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
              >
                Upload Article
              </button>
              <button
                type="button"
                className="flex-1 h-11 bg-white text-[#3498db] border border-[#3498db] rounded-lg hover:bg-[#f0f8ff] transition-all duration-300 font-semibold hover:scale-105 active:scale-100 text-sm sm:text-base"
                onClick={() => navigate("/contributor-dashboard")}
              >
                Back
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArticlePost;
